import {Component, inject, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {CommonModule, Location} from '@angular/common';
import {RouterLink} from '@angular/router';
import {DxLoadPanelModule, DxScrollViewModule, DxDataGridModule, DxToolbarModule, DxButtonModule, DxDateBoxModule, DxValidatorModule, DxValidationGroupModule, DxValidationGroupComponent} from "devextreme-angular";
import {AccessType, OrderAppointmentOverride, OrderAppointmentStatus, OrdersClient, OrderDto, OrderLineDto, OrderWmStatus, PermissionType, PreProductionStatus, ProductionStatus, UpdateOrderAppointmentOverrideCommand} from '@services';
import notify from "devextreme/ui/notify";
import {BehaviorSubject, Observable, of} from "rxjs";
import {catchError} from "rxjs/operators";
import {GridPageComponent} from "../../../../common/grid-page.component";
import EnumUtils from "../../../../../shared/enum-utils";
import {PreProductionStatusPipe} from "../../../../../pipes/pre-production-status.pipe";
import {ProductionStatusPipe} from "../../../../../pipes/production-status.pipe";
import {OrderExceptionPipe} from "../../../../../pipes/order-exception.pipe";
import {MissedOpportunityPipe} from "../../../../../pipes/missed-opportunity.pipe";
import {OrderAppointmentStatusPipe} from "../../../../../pipes/order-appointment-status.pipe";
import {EasternTimePipe} from "../../../../../pipes/eastern-time.pipe";
import {DATE_TIME_FORMAT} from "../../../../../shared/constants";
import {AuthService} from "@services";

@Component({
    selector: 'app-order-details',
    standalone: true,
    imports: [
        CommonModule,
        DxLoadPanelModule,
        DxScrollViewModule,
        DxDataGridModule,
        DxToolbarModule,
        DxButtonModule,
        DxDateBoxModule,
        DxValidatorModule,
        DxValidationGroupModule,
        RouterLink,
        PreProductionStatusPipe,
        ProductionStatusPipe,
        OrderExceptionPipe,
        MissedOpportunityPipe,
        OrderAppointmentStatusPipe,
        EasternTimePipe
    ],
    providers: [PreProductionStatusPipe, OrderExceptionPipe, MissedOpportunityPipe],
    templateUrl: './order-details.component.html',
    styles: [
        `.grid {
            min-height: calc(100vh - 360px);
        }`
    ]
})
export class OrderDetailsComponent extends GridPageComponent<OrderLineDto> implements OnChanges {
    @Input() number: string | null = null;
    @ViewChild('overrideValidation') validationGroup?: DxValidationGroupComponent;

    private location = inject(Location);
    private ordersClient = inject(OrdersClient);
    private authService = inject(AuthService);
    private preProductionStatusPipe = inject(PreProductionStatusPipe);
    private orderExceptionPipe = inject(OrderExceptionPipe);
    private missedOpportunityPipe = inject(MissedOpportunityPipe);

    protected isLoading = false;
    private orderSubject = new BehaviorSubject<OrderDto | null>(null);
    protected order$: Observable<OrderDto | null> = this.orderSubject.asObservable();
    protected offset: number = 155;

    dateTimeFormat = DATE_TIME_FORMAT;
    statusOptions = EnumUtils.getEnumOptions(OrderWmStatus);
    preProductionStatusOptions = EnumUtils.getEnumOptions(PreProductionStatus, (v) =>
        this.preProductionStatusPipe.transform(v),
    );

    protected override exportName: string = "OrderLines";

    readonly Override = OrderAppointmentOverride;
    readonly AppointmentStatus = OrderAppointmentStatus;
    protected canEdit = this.authService.checkPermission(PermissionType.Orders, AccessType.Edit);
    protected canViewSchedulingReferences = true;
    protected editOverride: OrderAppointmentOverride = OrderAppointmentOverride.None;
    protected editAppointment: Date | null = null;
    protected isSubmitting = false;
    private original = {override: OrderAppointmentOverride.None, appointment: null as Date | null};

    constructor() {
        super();
        this.init(OrderLineDto);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['number'] && this.number) {
            this.load();
        }
    }

    private load(): void {
        if (!this.number) return;
        this.isLoading = true;
        this.ordersClient.getOrder(this.number).pipe(
            catchError(() => {
                notify('Failed to load order', 'error', 3000);
                this.location.back();
                return of(null);
            }),
        ).subscribe((order) => {
            this.isLoading = false;
            if (order) {
                this.seedOverrideState(order);
                this.orderSubject.next(order);
            }
        });
    }

    private seedOverrideState(order: OrderDto): void {
        this.editOverride = order.appointmentOverride ?? OrderAppointmentOverride.None;
        this.editAppointment = this.editOverride === OrderAppointmentOverride.Manual && order.manualAppointment
            ? new Date(order.manualAppointment)
            : null;
        this.original = {override: this.editOverride, appointment: this.editAppointment};
    }

    protected selectOption(option: OrderAppointmentOverride): void {
        if (!this.canEdit) return;
        this.editOverride = this.editOverride === option ? OrderAppointmentOverride.None : option;
        if (this.editOverride !== OrderAppointmentOverride.Manual) {
            this.editAppointment = null;
        }
    }

    protected get isDataChanged(): boolean {
        return this.editOverride !== this.original.override
            || (this.editAppointment?.getTime() ?? null) !== (this.original.appointment?.getTime() ?? null);
    }

    protected save(): void {
        if (this.validationGroup && !this.validationGroup.instance.validate().isValid) return;

        const command = new UpdateOrderAppointmentOverrideCommand({
            number: this.number!,
            override: this.editOverride,
            manualAppointment: this.editOverride === OrderAppointmentOverride.Manual ? this.editAppointment! : undefined,
        });

        this.isSubmitting = true;
        this.ordersClient.updateOrderAppointmentOverride(command).subscribe({
            next: () => {
                this.isSubmitting = false;
                notify('Appointment override saved', 'success', 2000);
                this.load();
            },
            error: (errors) => {
                this.isSubmitting = false;
                const message = Array.isArray(errors) ? errors[0]?.message : 'Failed to save appointment override';
                notify(message ?? 'Failed to save appointment override', 'error', 4000);
            },
        });
    }

    protected cancel(): void {
        this.editOverride = this.original.override;
        this.editAppointment = this.original.appointment;
    }

    getStatusText(status?: OrderWmStatus): string {
        return this.statusOptions.find((o: any) => o.value === status)?.text ?? '';
    }

    calculateExceptionsCellValue = (line: OrderLineDto): string =>
        (line.exceptions ?? []).map(e => this.orderExceptionPipe.transform(e)).join(', ');

    calculateMissedOpportunityCellValue = (line: OrderLineDto): string =>
        (line.missedOpportunities ?? []).map(o => this.missedOpportunityPipe.transform(o)).join(', ');

    protected getPreProductionBadgeClass(status: PreProductionStatus): string {
        return EnumUtils.getPreProductionStatusBadgeClass(status);
    }

    protected getProductionBadgeClass(status: ProductionStatus | null): string | null {
        return status ? EnumUtils.getProductionStatusBadgeClass(status) : null;
    }

    protected getAppointmentStatusBadgeClass(status: OrderAppointmentStatus): string {
        return EnumUtils.getOrderAppointmentStatusBadgeClass(status);
    }

    protected appointmentHasTime(order: OrderDto): boolean {
        return order.appointmentStatus === OrderAppointmentStatus.ManualAppointmentSet
            || !!(order.sourceLocationAppointmentDate || order.deliveryDateTime || order.deliveryAppointment);
    }

    customizeSum(data: any) {
        return `Total: ${data.value}`;
    }
}
